import React from 'react';
import { useSearchParams } from 'react-router';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { FlowDetailMode } from './flow-detail.types';
import {
  Tune,
  FormatListBulletedAdd,
  ConnectWithoutContactOutlined,
  VideoSettings,
  CastOutlined,
  ArrowRightOutlined,
} from '@mui/icons-material';
import MediaPersonIcon from '@/assets/svg/media-person-icon';
import { IconButton, Stack, useTheme } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { useScreenSize } from '@/hooks/use-screen-size';

interface ActionButtonConfig {
  icon: React.ReactElement;
  isActive: boolean;
  onClick: () => void;
}

const activeColor = (isActive: boolean, theme: Theme) =>
  isActive ? theme.palette.info.main : theme.palette.text.secondary;

export default function Actions({
  setShowFlowDetails,
  selectedSlot,
  showFlowDetails,
}: {
  showFlowDetails: FlowDetailMode | undefined;
  setShowFlowDetails: React.Dispatch<React.SetStateAction<FlowDetailMode | undefined>>;
  selectedSlot?: FlowSlotDto;
}) {
  const theme = useTheme();
  const { isWideScreen } = useScreenSize({ breakpoint: 860 });
  const [searchParams] = useSearchParams();
  const viewParam = searchParams.get('view');
  const isScheduleTabActive =
    showFlowDetails === 'flowSchedule' ||
    (showFlowDetails === undefined && !!selectedSlot) ||
    viewParam === 'schedule' ||
    viewParam === 'slot';

  const handleButtonClick = (m: FlowDetailMode) => {
    setShowFlowDetails(m);
  };

  const { hasAccess: hasPerformFeatureAccess, isLoading: isPerformFeatureAccessLoading } = useFeatureAccess('perform');
  const performTabsAllowed = !isPerformFeatureAccessLoading && hasPerformFeatureAccess;

  const iconSize = 'medium' as const;
  const iconButtonSize = isWideScreen ? 'medium' : 'small';
  const baseActionButtons: ActionButtonConfig[] = [
    {
      icon: (
        <Tune
          fontSize={iconSize}
          color={showFlowDetails === 'flowSettings' ? 'info' : undefined}
          sx={showFlowDetails !== 'flowSettings' ? { color: 'text.secondary' } : undefined}
        />
      ),
      isActive: showFlowDetails === 'flowSettings',
      onClick: () => handleButtonClick('flowSettings'),
    },
    {
      icon: (
        <FormatListBulletedAdd
          fontSize={iconSize}
          sx={{
            color: activeColor(isScheduleTabActive, theme),
          }}
        />
      ),
      isActive: isScheduleTabActive,
      onClick: () => handleButtonClick('flowSchedule'),
    },
    {
      icon: (
        <MediaPersonIcon fontSize={iconSize} sx={{ color: activeColor(showFlowDetails === 'flowSlotAssets', theme) }} />
      ),
      isActive: showFlowDetails === 'flowSlotAssets',
      onClick: () => handleButtonClick('flowSlotAssets'),
    },
    {
      icon: (
        <ConnectWithoutContactOutlined
          fontSize={iconSize}
          sx={{ color: activeColor(showFlowDetails === 'flowContributors', theme) }}
        />
      ),
      isActive: showFlowDetails === 'flowContributors',
      onClick: () => handleButtonClick('flowContributors'),
    },
  ];
  const performActionButtons: ActionButtonConfig[] = performTabsAllowed
    ? [
        {
          icon: (
            <VideoSettings
              fontSize={iconSize}
              sx={{ color: activeColor(showFlowDetails === 'flowConfiguration', theme) }}
            />
          ),
          isActive: showFlowDetails === 'flowConfiguration',
          onClick: () => handleButtonClick('flowConfiguration'),
        },
        {
          icon: (
            <CastOutlined fontSize={iconSize} sx={{ color: activeColor(showFlowDetails === 'flowControl', theme) }} />
          ),
          isActive: showFlowDetails === 'flowControl',
          onClick: () => handleButtonClick('flowControl'),
        },
      ]
    : [];
  const actionButtons = [...baseActionButtons, ...performActionButtons];

  return (
    <Stack direction="row" spacing={isWideScreen ? 1 : 0.5} alignItems="center">
      {actionButtons.map((button, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <ArrowRightOutlined
              color="action"
              sx={{
                fontSize: isWideScreen ? 26 : 18,
                mx: isWideScreen ? 0.25 : 0.125,
                opacity: isWideScreen ? 0.85 : 1,
              }}
            />
          )}
          <IconButton
            size={iconButtonSize}
            onClick={button.onClick}
            aria-pressed={button.isActive}
            sx={{
              borderRadius: 1.5,
              ...(button.isActive
                ? {
                    'bgcolor': alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.28 : 0.14),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.36 : 0.2),
                    },
                  }
                : {}),
            }}
          >
            {button.icon}
          </IconButton>
        </React.Fragment>
      ))}
    </Stack>
  );
}
